<script lang="ts" module>
  export interface SwipeAction {
    label: string;
    /** Background colour of the action button. */
    color: string;
    onSelect: () => void;
  }

  /** Width of one revealed action button (px). */
  export const SWIPE_ACTION_WIDTH = 76;
  /** Horizontal travel (px) before a press becomes a swipe rather than a tap or a scroll. */
  const SWIPE_SLOP = 8;
  /** Leftward speed (px/ms) that opens the row whatever the distance. */
  const FLING_VELOCITY = 0.4;
</script>

<script lang="ts">
  /**
   * SwipeRow — a list row whose actions are revealed by swiping it left
   * (plans/mobile-v2.md §7: the one row gesture besides sheet dismiss).
   *
   * - `open` is controlled, so the list keeps a single row open: `onOpenChange`
   *   reports where a swipe ended (past 40% of the actions, or a fling).
   * - Vertical movement is left to the browser (`touch-action: pan-y`), so the
   *   list still scrolls; a mostly-vertical drag never becomes a swipe.
   * - While open, a tap on the row only closes it (the tap isn't passed on).
   * - The actions are unreachable by keyboard and screen readers (`inert`): swipe
   *   is a shortcut, and every action must also exist in the row's sheet.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    actions: SwipeAction[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: Snippet;
  }

  let { actions, open, onOpenChange, children }: Props = $props();

  let revealWidth = $derived(actions.length * SWIPE_ACTION_WIDTH);

  interface Gesture {
    pointerId: number;
    startX: number;
    startY: number;
    startOffset: number;
    lastX: number;
    lastTime: number;
    velocity: number; // px/ms, negative = leftward
    swiping: boolean;
    decided: boolean;
  }
  let gesture: Gesture | null = null;
  // px while a finger is dragging; null → the resting position for `open`
  let dragOffset = $state<number | null>(null);
  // A swipe ends with a click on whatever is under the finger; swallow that one.
  let swallowClick = false;

  let offset = $derived(dragOffset ?? (open ? -revealWidth : 0));

  function onPointerDown(event: PointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: open ? -revealWidth : 0,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocity: 0,
      swiping: false,
      decided: false,
    };
  }

  function onPointerMove(event: PointerEvent) {
    const g = gesture;
    if (!g || event.pointerId !== g.pointerId) return;
    const dx = event.clientX - g.startX;
    const dy = event.clientY - g.startY;

    if (!g.decided) {
      if (Math.abs(dx) < SWIPE_SLOP && Math.abs(dy) < SWIPE_SLOP) return;
      g.decided = true;
      g.swiping = Math.abs(dx) > Math.abs(dy);
      if (g.swiping) (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    }
    if (!g.swiping) return;

    const now = performance.now();
    const dt = now - g.lastTime;
    if (dt > 0) g.velocity = (event.clientX - g.lastX) / dt;
    g.lastX = event.clientX;
    g.lastTime = now;

    // A little rubber band past the actions; nothing to the right of closed.
    const min = -revealWidth - 16;
    dragOffset = Math.min(0, Math.max(min, g.startOffset + dx));
  }

  function finish(event: PointerEvent, cancelled: boolean) {
    const g = gesture;
    if (!g || event.pointerId !== g.pointerId) return;
    gesture = null;
    if (!g.swiping || dragOffset === null) return;

    swallowClick = true;
    // Not every swipe is followed by a click (the finger may have left the row):
    // don't let the flag swallow a later, unrelated tap.
    setTimeout(() => (swallowClick = false), 400);
    const current = dragOffset;
    dragOffset = null;
    if (cancelled) return;

    // A pause before letting go is not a fling.
    const velocity = performance.now() - g.lastTime > 100 ? 0 : g.velocity;
    const next =
      velocity <= -FLING_VELOCITY || (velocity < FLING_VELOCITY && current < -revealWidth * 0.4);
    if (next !== open) onOpenChange(next);
  }

  function onClickCapture(event: MouseEvent) {
    if (swallowClick) {
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (open) {
      event.preventDefault();
      event.stopPropagation();
      onOpenChange(false);
    }
  }

  function select(action: SwipeAction) {
    onOpenChange(false);
    action.onSelect();
  }
</script>

<div class="swipe-row" data-swipe-open={open}>
  <div class="actions" style="width: {revealWidth}px;" inert={!open}>
    {#each actions as action (action.label)}
      <button
        type="button"
        class="action"
        style="background: {action.color}; width: {SWIPE_ACTION_WIDTH}px;"
        onclick={() => select(action)}>{action.label}</button
      >
    {/each}
  </div>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="front"
    class:dragging={dragOffset !== null}
    style="transform: translateX({offset}px);"
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={(e) => finish(e, false)}
    onpointercancel={(e) => finish(e, true)}
    onclickcapture={onClickCapture}
  >
    {@render children()}
  </div>
</div>

<style>
  .swipe-row {
    position: relative;
    overflow: hidden;
  }

  .actions {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
  }

  .action {
    display: grid;
    place-items: center;
    padding: 0 4px;
    color: #fff;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .action:active {
    filter: brightness(0.92);
  }

  .front {
    position: relative;
    background: #fff;
    touch-action: pan-y;
    transition: transform 200ms ease-out;
  }

  .front.dragging {
    transition: none;
    user-select: none;
    -webkit-user-select: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .front {
      transition: none;
    }
  }
</style>
